/**
 * CompleteIterationModal Component
 *
 * Modal dialog for manually completing an iteration with a report.
 * Users can write or paste a report and optionally upload output files.
 */

import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDropzone } from 'react-dropzone';
import type { TaskIteration, IterationCreatedBy } from '@/types/tasks';
import { formatFileSize } from '@/types/tasks';
import { completeIteration, uploadIterationAttachment } from '@/services/api';

interface PendingFile {
  file: File;
  uploading: boolean;
  error?: string;
}

interface CompleteIterationModalProps {
  taskId: string;
  iteration: TaskIteration;
  isOpen: boolean;
  onClose: () => void;
  onCompleted: (iteration: TaskIteration) => void;
}

export default function CompleteIterationModal({
  taskId,
  iteration,
  isOpen,
  onClose,
  onCompleted,
}: CompleteIterationModalProps) {
  const { t } = useTranslation('tasks');
  const [report, setReport] = useState(iteration.report || '');
  const [reportCreatedBy, setReportCreatedBy] = useState<IterationCreatedBy>('user');
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const newFiles = acceptedFiles.map(file => ({
      file,
      uploading: false,
    }));
    setPendingFiles(prev => [...prev, ...newFiles]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxSize: 50 * 1024 * 1024, // 50MB
  });

  const removePendingFile = (index: number) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!report.trim()) {
      setError(t('completeIteration.reportContentRequired'));
      return;
    }

    setError(null);
    setIsSubmitting(true);

    try {
      // Upload output files first
      for (let i = 0; i < pendingFiles.length; i++) {
        const pf = pendingFiles[i];
        setPendingFiles(prev =>
          prev.map((f, idx) => idx === i ? { ...f, uploading: true } : f)
        );

        try {
          await uploadIterationAttachment(taskId, iteration.id, pf.file, 'output');
        } catch (uploadErr) {
          console.error('Failed to upload file:', uploadErr);
          setPendingFiles(prev =>
            prev.map((f, idx) =>
              idx === i ? { ...f, uploading: false, error: 'Upload failed' } : f
            )
          );
        }
      }

      // Complete the iteration
      const completedIteration = await completeIteration(taskId, iteration.id, {
        report: report.trim(),
        report_created_by: reportCreatedBy,
      });

      // Reset and close
      setReport('');
      setPendingFiles([]);
      onCompleted(completedIteration);
    } catch (err) {
      console.error('Failed to complete iteration:', err);
      setError(err instanceof Error ? err.message : 'Failed to complete iteration');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (isSubmitting) return;
    setReport(iteration.report || '');
    setPendingFiles([]);
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={handleClose}
        />

        {/* Modal */}
        <div className="relative inline-block bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:max-w-2xl sm:w-full">
          <form onSubmit={handleSubmit}>
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                {t('completeIteration.title', { number: iteration.iteration_number })}
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {t('completeIteration.subtitle')}
              </p>
            </div>

            {/* Body */}
            <div className="px-6 py-4 space-y-4 max-h-[60vh] overflow-y-auto">
              {/* Error */}
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
                  {error}
                </div>
              )}

              {/* Original Request */}
              {iteration.user_prompt && (
                <div className="p-3 bg-gray-50 rounded-md">
                  <h4 className="text-sm font-medium text-gray-700 mb-1">{t('completeIteration.originalRequest')}</h4>
                  <p className="text-sm text-gray-600 whitespace-pre-wrap">
                    {iteration.user_prompt}
                  </p>
                </div>
              )}

              {/* Report Input */}
              <div>
                <label
                  htmlFor="report"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  {t('completeIteration.reportLabel')} <span className="text-red-500">{t('completeIteration.reportRequired')}</span>
                </label>
                <textarea
                  id="report"
                  value={report}
                  onChange={(e) => setReport(e.target.value)}
                  rows={10}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 font-mono text-sm resize-y"
                  placeholder={t('completeIteration.reportPlaceholder')}
                  disabled={isSubmitting}
                  required
                />
                <p className="mt-1 text-xs text-gray-500">
                  {t('completeIteration.reportHint')}
                </p>
              </div>

              {/* Report Author */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('completeIteration.reportCreatedBy')}
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="report_created_by"
                      value="user"
                      checked={reportCreatedBy === 'user'}
                      onChange={() => setReportCreatedBy('user')}
                      disabled={isSubmitting}
                      className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">{t('completeIteration.reportCreatedByUser')}</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="report_created_by"
                      value="ai"
                      checked={reportCreatedBy === 'ai'}
                      onChange={() => setReportCreatedBy('ai')}
                      disabled={isSubmitting}
                      className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">{t('completeIteration.reportCreatedByAI')}</span>
                  </label>
                </div>
              </div>

              {/* Output Files */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('completeIteration.outputFilesLabel')}
                </label>
                <div
                  {...getRootProps()}
                  className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
                    isDragActive
                      ? 'border-green-400 bg-green-50'
                      : 'border-gray-300 hover:border-gray-400'
                  } ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <input {...getInputProps()} disabled={isSubmitting} />
                  <svg
                    className="mx-auto h-8 w-8 text-gray-400"
                    stroke="currentColor"
                    fill="none"
                    viewBox="0 0 48 48"
                  >
                    <path
                      d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <p className="mt-1 text-sm text-gray-600">
                    {isDragActive
                      ? t('completeIteration.outputFilesDropActive')
                      : t('completeIteration.outputFilesDropInactive')}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {t('completeIteration.outputFilesHint')}
                  </p>
                </div>

                {/* Pending Files List */}
                {pendingFiles.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {pendingFiles.map((pf, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-2 bg-green-50 rounded-md"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-green-600">📤</span>
                          <span className="text-sm text-gray-700 truncate">
                            {pf.file.name}
                          </span>
                          <span className="text-xs text-gray-500">
                            {formatFileSize(pf.file.size)}
                          </span>
                          {pf.uploading && (
                            <span className="text-xs text-blue-600">{t('completeIteration.uploading')}</span>
                          )}
                          {pf.error && (
                            <span className="text-xs text-red-600">{pf.error}</span>
                          )}
                        </div>
                        {!pf.uploading && (
                          <button
                            type="button"
                            onClick={() => removePendingFile(index)}
                            className="p-1 text-red-600 hover:text-red-700 hover:bg-red-50 rounded"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3">
              <button
                type="button"
                onClick={handleClose}
                disabled={isSubmitting}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
              >
                {t('completeIteration.cancel')}
              </button>
              <button
                type="submit"
                disabled={isSubmitting || !report.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    {t('completeIteration.completing')}
                  </>
                ) : (
                  t('completeIteration.submit')
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
